import type React from 'react'
import { FONT, R, T, sectionLabel } from '../builderStyles'

// Small presentational pieces shared by the builder steps (prototype `.card`,
// `.section-label`, `.radio-opt`, notes).

export const CheckSvg: React.FC<{ size?: number; strokeWidth?: number }> = ({
  size = 12,
  strokeWidth = 3,
}) => (
  <svg
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
)

export const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div style={sectionLabel}>{children}</div>

// Prototype `.card` with header (title / subtitle / right slot) + body.
export const StepCard: React.FC<{
  title: string
  subtitle?: string
  icon?: React.ReactNode
  right?: React.ReactNode
  children: React.ReactNode
}> = ({ title, subtitle, icon, right, children }) => (
  <div
    style={{
      background: T.white,
      border: `1px solid ${T.gray200}`,
      borderRadius: R.lg,
      overflow: 'hidden',
      fontFamily: FONT,
    }}
  >
    <div
      style={{
        padding: '16px 20px',
        borderBottom: `1px solid ${T.gray100}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: T.gray800,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {icon && (
            <span style={{ color: T.gray500, display: 'inline-flex' }}>
              {icon}
            </span>
          )}
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12, color: T.gray400, marginTop: 2 }}>
            {subtitle}
          </div>
        )}
      </div>
      {right}
    </div>
    <div style={{ padding: 20 }}>{children}</div>
  </div>
)

// Prototype `.radio-opt`.
export const RadioOpt: React.FC<{
  selected: boolean
  disabled?: boolean
  label: string
  desc: string
  onSelect: () => void
}> = ({ selected, disabled, label, desc, onSelect }) => (
  <button
    type="button"
    role="radio"
    className="bsb-card"
    aria-checked={selected}
    disabled={disabled}
    onClick={onSelect}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 14px',
      border: `2px solid ${selected ? T.blue : T.gray200}`,
      borderRadius: R.sm,
      background: selected ? T.blueBg : T.white,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      textAlign: 'left',
      width: '100%',
      fontFamily: FONT,
    }}
  >
    <span
      style={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: `2px solid ${selected ? T.blue : T.gray300}`,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      {selected && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: T.blue,
          }}
        />
      )}
    </span>
    <span style={{ minWidth: 0 }}>
      <span
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          color: selected ? T.blue : T.gray700,
        }}
      >
        {label}
      </span>
      <span
        style={{
          display: 'block',
          fontSize: 11.5,
          color: T.gray400,
          marginTop: 2,
        }}
      >
        {desc}
      </span>
    </span>
  </button>
)

export const NoteBanner: React.FC<{
  tone?: 'amber' | 'purple' | 'blue'
  children: React.ReactNode
}> = ({ tone = 'amber', children }) => {
  const c =
    tone === 'purple'
      ? { bg: T.purpleBg, fg: T.purple, border: '#DDD6FE' }
      : tone === 'blue'
        ? { bg: T.blueBg, fg: T.blue, border: '#BFDBFE' }
        : { bg: T.amberBg, fg: T.amber, border: '#FDE68A' }
  return (
    <div
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.fg,
        borderRadius: R.md,
        padding: '12px 16px',
        fontSize: 12.5,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  )
}

// Disables every form control inside without changing layout (read-only
// variants: locked content, launched schedule).
export const LockedFieldset: React.FC<{
  disabled: boolean
  children: React.ReactNode
}> = ({ disabled, children }) => (
  <fieldset
    disabled={disabled}
    style={{
      border: 'none',
      margin: 0,
      padding: 0,
      minWidth: 0,
      opacity: disabled ? 0.65 : 1,
    }}
  >
    {children}
  </fieldset>
)

export const Stack: React.FC<{ gap?: number; children: React.ReactNode }> = ({
  gap = 16,
  children,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap }}>
    {children}
  </div>
)
